import { useEffect, useRef, useCallback } from 'react';
import { useSpotify } from '../context/SpotifyContext';

/**
 * Component that syncs Spotify playback state globally
 * Place this inside SpotifyProvider to enable player bar on all pages
 */
const SpotifyGlobalSync = () => {
  const {
    setCurrentTrack,
    setIsPlaying,
    setShowPlayerBar,
    setAccessToken,
    setProgressMs,
    setDurationMs,
    isPlaying,
    currentTrack,
    durationMs,
    showPlayerBar,
  } = useSpotify();

  const pollInterval = useRef(null);
  const progressInterval = useRef(null);
  const hasInitializedPlayerBar = useRef(false);

  const API_URL = (() => {
    const envUrl = window.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || '';
    return envUrl.replace(/\/api$/, '');
  })();

  const fetchCurrentTrack = useCallback(async () => {
    const token = localStorage.getItem('spotify_access_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/spotify/currently-playing?access_token=${encodeURIComponent(token)}`);
      if (!response.ok) return;
      
      const res = await response.json();
      
      if (res.track) {
        setCurrentTrack({
          name: res.track.name,
          artists: res.track.artist,
          album: res.track.album,
          image: res.track.album_art || ''
        });
        setIsPlaying(res.is_playing);
        setAccessToken(token);
        setProgressMs(res.track.progress || 0);
        setDurationMs(res.track.duration || 0);
        
        // Only auto-show player bar on FIRST track detection, not on every poll
        // This respects user's choice to minimize the player
        if (!hasInitializedPlayerBar.current) {
          setShowPlayerBar(true);
          hasInitializedPlayerBar.current = true;
        }
      }
    } catch (err) {
      // Silently fail - don't spam console
    }
  }, [API_URL, setCurrentTrack, setIsPlaying, setShowPlayerBar, setAccessToken, setProgressMs, setDurationMs]);

  // Poll for current track every 5 seconds
  useEffect(() => {
    const token = localStorage.getItem('spotify_access_token');
    if (!token) return;

    // Initial fetch
    fetchCurrentTrack();

    // Set up polling
    pollInterval.current = setInterval(fetchCurrentTrack, 5000);

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [fetchCurrentTrack]);

  // Local progress interpolation for smooth progress bar
  useEffect(() => {
    if (!isPlaying || !currentTrack) {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      return;
    }

    progressInterval.current = setInterval(() => {
      setProgressMs(prev => {
        const newProgress = prev + 1000;
        // Don't exceed track duration
        if (durationMs && newProgress >= durationMs) {
          return prev;
        }
        return newProgress;
      });
    }, 1000);

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [isPlaying, currentTrack, durationMs, setProgressMs]);

  // This component doesn't render anything
  return null;
};

export default SpotifyGlobalSync;
